// "Eklenti Kullanımı" on the Delivery Log page: who the Outlook add-in has
// actually run for in the last 30 days, against the Microsoft 365 directory
// (backend: signatures/views.py::signature_addin_usage). Microsoft doesn't
// expose the Integrated Apps assignment list through Graph, so "running" is
// measured — anyone it ran for called us — and "not seen" can mean not
// deployed yet, or simply no mail written since.
import React, { useEffect, useMemo, useState } from 'react';
import { api, endpoints } from '../config/api';
import { Badge, Button, Spinner } from './ui';
import DashboardCard from './DashboardCard';
import { ReactComponent as SuccessIcon } from '../icons/success-circle.svg';
import { ReactComponent as UserIcon } from '../icons/user.svg';
import { ReactComponent as ErrorIcon } from '../icons/error-circle.svg';
import { useT } from '../i18n';
import './AddinUsage.css';

const PAGE = 50;

const messages = {
  title: { tr: 'Eklenti Kullanımı', en: 'Add-in Usage' },
  hint: {
    tr: 'Son 30 günde Outlook eklentisinin çalıştığı kullanıcılar, Microsoft 365 dizinindeki kullanıcılarla karşılaştırılır. Microsoft, eklentinin yönetim merkezinden kimlere atandığı bilgisini paylaşmadığı için burada eklentinin gerçekten çalıştığı kişiler sayılır. "Görülmedi", eklentinin o kişiye henüz ulaşmadığı (dağıtım 1-2 gün sürebilir) ya da kişinin o tarihten beri mail yazmadığı anlamına gelir.',
    en: 'Users the Outlook add-in has run for in the last 30 days, compared with your Microsoft 365 directory. Microsoft doesn’t share who the add-in is assigned to in the admin center, so this counts the people it actually ran for. “Not seen” means the add-in hasn’t reached that person yet (deployment can take 1-2 days) or they haven’t written a mail since.',
  },
  running: { tr: 'Eklentisi Çalışan Kullanıcı', en: 'Users Running the Add-in' },
  ofDirectory: { tr: '{active} / {total}', en: '{active} / {total}' },
  last7: { tr: 'Son 7 Günde Görülen', en: 'Seen in the Last 7 Days' },
  notSeen: { tr: 'Henüz Görülmeyen', en: 'Not Seen Yet' },
  versions: { tr: 'Eklenti sürümleri:', en: 'Add-in versions:' },
  oldVersion: { tr: '1.0.34 veya öncesi', en: '1.0.34 or earlier' },
  people: { tr: '{n} kişi', en: '{n} people' },
  all: { tr: 'Tümü', en: 'All' },
  seen: { tr: 'Çalışıyor', en: 'Running' },
  unseen: { tr: 'Görülmedi', en: 'Not seen' },
  search: { tr: 'Ad veya e-posta ara', en: 'Search name or email' },
  user: { tr: 'Kullanıcı', en: 'User' },
  group: { tr: 'Grup', en: 'Group' },
  lastSeen: { tr: 'Son Görülme', en: 'Last Seen' },
  client: { tr: 'İstemci', en: 'Client' },
  version: { tr: 'Sürüm', en: 'Version' },
  state: { tr: 'Durum', en: 'Status' },
  clients: {
    tr: { outlook: 'Outlook', outlook_mobile: 'Outlook Mobil', gmail: 'Gmail' },
    en: { outlook: 'Outlook', outlook_mobile: 'Outlook Mobile', gmail: 'Gmail' },
  },
  statusSuccess: { tr: 'İmza ekleniyor', en: 'Signing' },
  statusUnassigned: { tr: 'Şablon atanmamış', en: 'No template assigned' },
  statusFailure: { tr: 'Hata', en: 'Error' },
  statusUnseen: { tr: 'Görülmedi', en: 'Not seen' },
  notInDirectory: { tr: 'Dizinde yok', en: 'Not in directory' },
  noMatch: { tr: 'Eşleşen kullanıcı yok.', en: 'No matching users.' },
  more: { tr: 'Daha fazla göster ({n})', en: 'Show more ({n})' },
  loadError: { tr: 'Eklenti kullanımı yüklenemedi.', en: 'Couldn’t load add-in usage.' },
};

const STATUS_BADGE = {
  success: ['success', 'statusSuccess'],
  unassigned: ['warning', 'statusUnassigned'],
  failure: ['danger', 'statusFailure'],
};

export default function AddinUsage() {
  const { t, formatDateTime, formatNumber } = useT(messages);
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [shown, setShown] = useState(PAGE);

  useEffect(() => {
    let cancelled = false;
    api.get(endpoints.signatures.deliveryLog.addinUsage)
      .then((res) => { if (!cancelled) setData(res); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, []);

  const rows = useMemo(() => {
    if (!data) return [];
    const q = query.trim().toLowerCase();
    return data.users.filter((u) => (
      (filter === 'all' || (filter === 'seen' ? !!u.last_seen : !u.last_seen)) &&
      (!q || u.email.toLowerCase().includes(q) || (u.name || '').toLowerCase().includes(q))
    ));
  }, [data, filter, query]);

  useEffect(() => { setShown(PAGE); }, [filter, query]);

  if (failed) return <p className="addin-usage-empty">{t('loadError')}</p>;
  if (!data) return <div className="page-loading"><Spinner /></div>;

  const versions = Object.entries(data.versions || {})
    .sort(([a], [b]) => (b || '0').localeCompare(a || '0', undefined, { numeric: true }));

  return (
    <section className="addin-usage">
      <p className="addin-usage-hint">{t('hint')}</p>
      <div className="delivery-log-tiles">
        <DashboardCard CardIcon={<SuccessIcon />} title={t('running')}
          count={t('ofDirectory', { active: formatNumber(data.active), total: formatNumber(data.directory_total) })} />
        <DashboardCard CardIcon={<UserIcon />} title={t('last7')} count={formatNumber(data.active_7d)} />
        <DashboardCard CardIcon={<ErrorIcon />} title={t('notSeen')} count={formatNumber(data.not_seen)} />
      </div>
      {versions.length > 0 && (
        <p className="addin-usage-versions">
          <span>{t('versions')}</span>
          {versions.map(([v, n]) => (
            <span key={v || 'old'} className="addin-usage-version">
              {v || t('oldVersion')} · {t('people', { n: formatNumber(n) })}
            </span>
          ))}
        </p>
      )}

      <div className="addin-usage-toolbar">
        <div className="addin-usage-filter" role="tablist">
          {['all', 'seen', 'unseen'].map((f) => (
            <button key={f} type="button" role="tab" aria-selected={filter === f}
              className={filter === f ? 'is-on' : ''} onClick={() => setFilter(f)}>
              {t(f)}
            </button>
          ))}
        </div>
        <input type="search" className="addin-usage-search" placeholder={t('search')}
          value={query} onChange={(e) => setQuery(e.target.value)} />
      </div>

      <div className="addin-usage-table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{t('user')}</th>
              <th>{t('group')}</th>
              <th>{t('lastSeen')}</th>
              <th>{t('client')}</th>
              <th>{t('version')}</th>
              <th>{t('state')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={6} className="data-table-empty">{t('noMatch')}</td></tr>
            )}
            {rows.slice(0, shown).map((u) => {
              const [variant, label] = STATUS_BADGE[u.status] || ['info', 'statusUnseen'];
              return (
                <tr key={u.email}>
                  <td>
                    <div className="addin-usage-user">
                      <span>{u.name || u.email}</span>
                      {u.name && <small>{u.email}</small>}
                      {!u.in_directory && <small className="addin-usage-outside">{t('notInDirectory')}</small>}
                    </div>
                  </td>
                  <td>{u.group || '—'}</td>
                  <td>{u.last_seen ? formatDateTime(u.last_seen, { dateStyle: 'medium', timeStyle: 'short' }) : '—'}</td>
                  <td>{u.clients.length ? u.clients.map((c) => t('clients')[c] || c).join(', ') : '—'}</td>
                  <td>{u.last_seen ? (u.addin_version || t('oldVersion')) : '—'}</td>
                  <td><Badge variant={variant}>{t(label)}</Badge></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {rows.length > shown && (
        <div className="addin-usage-more">
          <Button type="button" variant="secondary" size="md" onClick={() => setShown(shown + PAGE)}>
            {t('more', { n: formatNumber(rows.length - shown) })}
          </Button>
        </div>
      )}
    </section>
  );
}
